import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { NEW_PRIVACY_POLICY_URL } from '../../../constants';
import { CookieIcon } from './CookieIcon';
import style from './CookieBanner.module.css';

interface PrivacyPolicyLinkProps {
    href: string;
    children: React.ReactNode;
}

const PrivacyPolicyLink = ({ href, children }: PrivacyPolicyLinkProps): JSX.Element => (
    <a href={href} target="_blank" rel="noopener noreferrer" className={style.link}>
        {children}
    </a>
);

interface CookieBannerProps {
    handleClick: () => void;
    open: boolean;
    handleClose: () => void;
    onSubmitCookie: () => void;
}

const CookieBanner = ({
    handleClick,
    open,
    handleClose,
    onSubmitCookie,
}: CookieBannerProps): JSX.Element => {
    const { t } = useTranslation();
    const popoverRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Click-outside detection
    useEffect(() => {
        if (!open) return;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                popoverRef.current &&
                !popoverRef.current.contains(target) &&
                buttonRef.current &&
                !buttonRef.current.contains(target)
            ) {
                handleClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open, handleClose]);

    return (
        <div className={style.cookieBox}>
            <div className={style.cookieButtonWrapper}>
                <button
                    ref={buttonRef}
                    className={style.cookieButton}
                    onClick={handleClick}
                >
                    <CookieIcon />
                    {t('Cookies Policy')}
                    <span className={style.arrowIcon}>&#9660;</span>
                </button>
                {open && (
                    <div ref={popoverRef} className={style.popover}>
                        <button className={style.closeButton} onClick={handleClose}>
                            &#10005;
                        </button>
                        <div className={style.typographyBox}>
                            <div className={style.titleText}>
                                {t('We value your privacy')}
                            </div>
                            <p className={style.bodyText}>
                                {t(`Part 1`)}{' '}
                                <PrivacyPolicyLink
                                    href={`${NEW_PRIVACY_POLICY_URL}/privacypolicy.html`}
                                >
                                    {t('Privacy Policy')}
                                </PrivacyPolicyLink>
                                {t(`Part 2`)}{' '}
                                <PrivacyPolicyLink
                                    href={`${NEW_PRIVACY_POLICY_URL}/termsofuse.html`}
                                >
                                    {t('Terms of Use')}
                                </PrivacyPolicyLink>{' '}
                                {t(`Part 3`)}{' '}
                                <PrivacyPolicyLink
                                    href={`${NEW_PRIVACY_POLICY_URL}/privacypolicy.html`}
                                >
                                    {t('Privacy Policy')}
                                </PrivacyPolicyLink>
                            </p>
                        </div>
                        <div className={style.insideAcceptDiv}>
                            <button className={style.acceptButton} onClick={onSubmitCookie}>
                                {t('Accept')}
                            </button>
                        </div>
                    </div>
                )}
            </div>
            <button className={style.acceptButton} onClick={onSubmitCookie}>{t('Accept')}</button>
        </div>
    );
};

export { CookieBanner };
